import {
  CreateChatRequestSchema,
  CreateChatResponseSchema,
  CreateWorkstreamRequestSchema,
  CreateWorkstreamResponseSchema,
  ListWorkstreamsResponseSchema,
  ProblemDetailsSchema,
  type CreateChatRequest,
  type CreateChatResponse,
  type CreateWorkstreamRequest,
  type CreateWorkstreamResponse,
  type ListWorkstreamsResponse,
  type ProblemDetails,
} from '@agentisai/shared-types';
import { config } from '../config';

const API_PREFIX = '/api/v1';

/** Typed error thrown on any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

interface RequestOptions {
  signal?: AbortSignal;
  getAccessToken: () => Promise<string>;
}

async function request<TBody, TResponse>(
  path: string,
  body: TBody,
  parseResponse: (raw: unknown) => TResponse,
  { signal, getAccessToken }: RequestOptions,
): Promise<TResponse> {
  const token = await getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  const raw: unknown = await response.json();

  if (!response.ok) {
    const problem = ProblemDetailsSchema.parse(raw);
    throw new ApiError(response.status, problem);
  }

  return parseResponse(raw);
}

export function sendChatTurn(
  body: CreateChatRequest,
  options: RequestOptions,
): Promise<CreateChatResponse> {
  const validated = CreateChatRequestSchema.parse(body);
  return request(
    '/workstream/create/chat',
    validated,
    (raw) => CreateChatResponseSchema.parse(raw),
    options,
  );
}

export function createWorkstream(
  body: CreateWorkstreamRequest,
  options: RequestOptions,
): Promise<CreateWorkstreamResponse> {
  const validated = CreateWorkstreamRequestSchema.parse(body);
  return request(
    '/workstream/create',
    validated,
    (raw) => CreateWorkstreamResponseSchema.parse(raw),
    options,
  );
}

/**
 * `GET /api/v1/workstream` — the caller's workstreams (S-C1).
 *
 * Parsed through `ListWorkstreamsResponseSchema` (A2 / ADR-0033) so a server
 * shape drift surfaces at the boundary instead of nullable-everything at
 * render. Non-2xx throws `ApiError` for the existing error sink to route.
 */
export async function listWorkstreams(options: RequestOptions): Promise<ListWorkstreamsResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}/workstream`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal: options.signal,
  });

  const raw: unknown = await response.json();
  if (!response.ok) {
    const problem = ProblemDetailsSchema.parse(raw);
    throw new ApiError(response.status, problem);
  }
  return ListWorkstreamsResponseSchema.parse(raw);
}
